import * as THREE from 'three';
import { ownFigureMaterials, shareFigureMaterials } from './figure-materials.js';

/** A hidden hero is drawn this dark (its surfaces multiplied by this colour) and about this solid, flickering round it. */
const SHADOW = new THREE.Color(0x30343d), OPACITY = .55, FLICKER = .12;
/** Game seconds it takes to sink into shadow or come back out of it. */
const FADE_SECONDS = .35;

interface Surface { material: THREE.MeshStandardMaterial; color: THREE.Color; opacity: number; transparent: boolean }
interface Shadowed { group: THREE.Object3D; surfaces: Surface[]; depth: number; at: number; seed: number }

/**
 * Hidden heroes as dark, faintly flickering shapes: the figure's own copies of its surfaces (`ownFigureMaterials`) are darkened and
 * made see-through, and their opacity wavers a little every frame. Colour and opacity changes need no new shader; only the switch to
 * transparent does, once as the hero hides and once as it comes back. Presentation only: the snapshot's `hidden` decides who.
 */
export class ShadowFigures {
  private shadowed = new Map<string, Shadowed>();

  update(hidden: ReadonlySet<string>, groups: ReadonlyMap<string, THREE.Object3D>, gameTime: number) {
    for (const id of hidden) {
      const group = groups.get(id);
      if (group && !this.shadowed.has(id)) this.shadowed.set(id, this.take(group, id, gameTime));
    }
    for (const [id, shadow] of this.shadowed) {
      if (groups.get(id) !== shadow.group) { this.release(id); continue; }
      const step = Math.max(0, gameTime - shadow.at) / FADE_SECONDS; shadow.at = gameTime;
      shadow.depth = hidden.has(id) ? Math.min(1, shadow.depth + step) : Math.max(0, shadow.depth - step);
      if (shadow.depth <= 0 && !hidden.has(id)) { this.release(id); continue; }
      // Two uneven waves and a rare deeper dip, so the shape wavers like something half seen rather than blinking.
      const t = gameTime + shadow.seed * 17;
      const flicker = Math.sin(t * 11.3) * .5 + Math.sin(t * 27.1 + 1.7) * .3 + (Math.sin(t * 2.3) > .93 ? -.8 : 0);
      const opacity = 1 - shadow.depth * (1 - (OPACITY + FLICKER * flicker));
      for (const surface of shadow.surfaces) {
        surface.material.color.copy(surface.color).lerp(SHADOW, shadow.depth);
        surface.material.opacity = surface.opacity * opacity;
      }
    }
  }
  clear() { for (const id of [...this.shadowed.keys()]) this.release(id); }

  private take(group: THREE.Object3D, id: string, gameTime: number): Shadowed {
    ownFigureMaterials(group);
    const surfaces: Surface[] = [], seen = new Set<THREE.Material>();
    group.traverse(node => {
      if (!(node instanceof THREE.Mesh)) return;
      for (const material of [node.material as THREE.Material | THREE.Material[]].flat()) {
        if (seen.has(material) || !(material instanceof THREE.MeshStandardMaterial)) continue;
        seen.add(material);
        surfaces.push({ material, color: material.color.clone(), opacity: material.opacity, transparent: material.transparent });
        if (!material.transparent) { material.transparent = true; material.needsUpdate = true; }
      }
    });
    let seed = 0; for (const char of id) seed = (seed * 31 + char.charCodeAt(0)) % 997;
    return { group, surfaces, depth: 0, at: gameTime, seed: seed / 997 };
  }
  private release(id: string) {
    const shadow = this.shadowed.get(id); if (!shadow) return;
    for (const { material, color, opacity, transparent } of shadow.surfaces) {
      material.color.copy(color); material.opacity = opacity;
      if (material.transparent !== transparent) { material.transparent = transparent; material.needsUpdate = true; }
    }
    shareFigureMaterials(shadow.group);
    this.shadowed.delete(id);
  }
}
